import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Form, Input, InputNumber, Modal, Space, Switch, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useCrmActivityTypes, useSaveCrmActivityType } from '@/hooks/useCrmActivities';
import crmActivityService, { type CrmActivityType } from '@/services/crmActivity.service';

const { Title, Paragraph } = Typography;

/** Stale-deal threshold and the Monday weekly performance email (company settings). */
const CrmPipelineSettingsCard: React.FC = () => {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [form] = Form.useForm<{ stale_deal_days: number; weekly_performance_email: boolean }>();
  const { data, isLoading } = useQuery({ queryKey: ['crm', 'settings'], queryFn: () => crmActivityService.getSettings() });
  useEffect(() => {
    if (data) form.setFieldsValue(data);
  }, [data, form]);
  const saveMut = useMutation({
    mutationFn: (v: { stale_deal_days: number; weekly_performance_email: boolean }) => crmActivityService.saveSettings(v),
    onSuccess: (d) => {
      qc.setQueryData(['crm', 'settings'], d);
      message.success('CRM settings saved');
    },
    onError: (e: unknown) =>
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to save'),
  });

  return (
    <Card size="small" title="Pipeline settings" loading={isLoading} style={{ marginBottom: 16 }}>
      <Form form={form} layout="inline" onFinish={(v) => saveMut.mutate(v)}>
        <Form.Item
          name="stale_deal_days"
          label="Stale deal after"
          tooltip="Open opportunities with no sales activity for this many days are flagged in the Command Center and the weekly report."
          rules={[{ required: true }]}
        >
          <InputNumber min={1} max={365} addonAfter="days" style={{ width: 150 }} />
        </Form.Item>
        <Form.Item
          name="weekly_performance_email"
          label="Email weekly performance to team managers"
          tooltip="Every Monday 07:00 each sales team manager is notified in-app; when on, the PDF is also emailed."
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={saveMut.isPending}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

const CrmActivityTypesPage: React.FC = () => {
  const { data: types = [], isLoading } = useCrmActivityTypes({ include_inactive: true });
  const save = useSaveCrmActivityType();
  const [editing, setEditing] = useState<CrmActivityType | 'new' | null>(null);
  const [form] = Form.useForm();

  const open = (row: CrmActivityType | 'new') => {
    form.resetFields();
    if (row === 'new') {
      form.setFieldsValue({
        counts_as_customer_contact: true,
        requires_outcome: false,
        is_active: true,
        sort_order: (types.length + 1) * 10,
      });
    } else {
      form.setFieldsValue(row);
    }
    setEditing(row);
  };

  const submit = async () => {
    const v = await form.validateFields();
    await save.mutateAsync({ id: editing === 'new' ? undefined : editing?.id, body: v });
    setEditing(null);
  };

  const columns: ColumnsType<CrmActivityType> = [
    { title: 'Code', dataIndex: 'code', width: 160 },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (v: string, r) => (
        <Space>
          {v}
          {r.is_system && <Tag>System</Tag>}
        </Space>
      ),
    },
    {
      title: 'Customer contact',
      dataIndex: 'counts_as_customer_contact',
      width: 150,
      render: (v: boolean) => (v ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Outcome required',
      dataIndex: 'requires_outcome',
      width: 150,
      render: (v: boolean) => (v ? <Tag color="orange">Yes</Tag> : <Tag>No</Tag>),
    },
    { title: 'Order', dataIndex: 'sort_order', width: 90 },
    {
      title: 'Status',
      dataIndex: 'is_active',
      width: 100,
      render: (v: boolean) => (v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, r) =>
        r.is_system ? null : (
          <Button type="link" size="small" onClick={() => open(r)}>
            Edit
          </Button>
        ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Sales activity types
          </Title>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            The kinds of activity salespeople log. "Customer contact" types update a lead&apos;s last contact date and
            count towards activity targets.
          </Paragraph>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>
          New type
        </Button>
      </Space>
      <CrmPipelineSettingsCard />
      <Card size="small" title="Activity types">
        <Table<CrmActivityType>
          rowKey="id"
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={types}
          pagination={false}
        />
      </Card>

      <Modal
        open={!!editing}
        title={editing === 'new' ? 'New activity type' : 'Edit activity type'}
        okText="Save"
        onOk={submit}
        onCancel={() => setEditing(null)}
        confirmLoading={save.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="code"
            label="Code"
            rules={[
              { required: true },
              { pattern: /^[A-Z0-9_]+$/, message: 'Upper-case letters, digits and underscores only' },
            ]}
          >
            <Input disabled={editing !== 'new'} maxLength={40} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="counts_as_customer_contact" label="Counts as customer contact" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="requires_outcome" label="Outcome required when logging" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="sort_order" label="Display order">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CrmActivityTypesPage;
